import { useEffect, useState } from 'react';
import { trackVisibleSections } from '../../measurement/sectionTracking';
import '../EcommerceAuditLanding/EcommerceAuditLanding.css';

const sectionName = 'landing';
const subsection1 = 'partner_agencias';

const pushDataLayerEvent = ({
  event,
  subsection2 = '',
  elementCategory = '',
  elementName = ''
}) => {
  const fullHierarchy = `${sectionName}:${subsection1}:${subsection2}`;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    section: sectionName,
    subsection_1: subsection1,
    subsection_2: subsection2,
    full_hierarchy: fullHierarchy,
    element_category: elementCategory,
    element_name: elementName || subsection2,
    page_url: window.location.href
  });
};

const PartnerAgenciesLanding = () => {
  const [status, setStatus] = useState('idle');
  const [hasStartedForm, setHasStartedForm] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showError, setShowError] = useState(false);

  const scrollToSection = (sectionId) => {
    window.isProgrammaticScroll = true;

    const sectionElement = document.getElementById(sectionId);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth' });
    }

    window.clearTimeout(window.programmaticScrollTimeout);
    window.programmaticScrollTimeout = window.setTimeout(() => {
      window.isProgrammaticScroll = false;
    }, 1500);
  };

  const handleCtaClick = ({
    elementName = 'contactar_partner',
    subsection2,
    targetId = 'partner-agencias-contact-form'
  }) => {
    pushDataLayerEvent({
      event: 'click_cta',
      subsection2,
      elementCategory: 'cta',
      elementName
    });

    scrollToSection(targetId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!privacyAccepted) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setStatus('loading');

    const formData = new FormData(e.target);
    const serviceInterest = formData.get('service_interest');
    const data = {
      form_id: 'partner-agencias',
      subject: 'Nueva solicitud - Partner white-label para agencias - DataDos',
      name: formData.get('name'),
      company: formData.get('company'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      service_interest: serviceInterest,
      message: formData.get('message'),
      hp: formData.get('b_name'),
      website: 'datados.es/partner-agencias'
    };

    try {
      const response = await fetch('https://datados.es/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (result.ok) {
        pushDataLayerEvent({
          event: 'form',
          subsection2: 'formulario_contacto',
          elementCategory: 'form-submit',
          elementName: 'enviar_solicitud'
        });

        setStatus('success');
        setPrivacyAccepted(false);
        setShowError(false);
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const handleFormInteraction = () => {
    if (hasStartedForm) {
      return;
    }

    pushDataLayerEvent({
      event: 'form',
      subsection2: 'formulario_contacto',
      elementCategory: 'form-start',
      elementName: 'comienzo_formulario'
    });
    setHasStartedForm(true);
  };

  useEffect(() => {
    return trackVisibleSections({
      rootSelector: '.ecommerce-audit-page.partner-agencies-page',
      subsection1: 'partner_agencias'
    });
  }, []);

  useEffect(() => {
    if (status !== 'success') {
      return;
    }

    setHasStartedForm(false);

    pushDataLayerEvent({
      event: 'section_view',
      subsection2: 'solicitud_enviada'
    });
  }, [status]);

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content');

    document.title = 'DataDos | Medición white-label para agencias';
    description?.setAttribute(
      'content',
      'Servicio white-label de GA4, GTM, eventos de conversión, Google Ads y dashboards para agencias de marketing, estudios web y freelancers.'
    );

    const items = document.querySelectorAll('.ea-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    items.forEach((item) => observer.observe(item));

    return () => {
      document.title = previousTitle;
      if (previousDescription) {
        description?.setAttribute('content', previousDescription);
      }
      observer.disconnect();
    };
  }, []);

  return (
    <div className="ecommerce-audit-page partner-agencies-page">
      <main>
        <section className="ea-hero" data-section="hero">
          <div className="ea-container ea-hero-grid">
            <div className="ea-reveal">
              <p className="ea-badge">Partner white-label para agencias</p>
              <h1>Medición white-label para agencias, estudios web y freelancers</h1>
              <p className="ea-hero-question">
                Ofrece analítica profesional a tus clientes sin incorporar un perfil técnico interno.
              </p>
              <p className="ea-hero-subtitle">
                Configuración de GA4, GTM, eventos de conversión, Google Ads y dashboards bajo tu marca.
                Tu agencia mantiene la relación con el cliente y DataDos entra por detrás como apoyo técnico.
              </p>
              <div className="ea-final-actions ea-hero-actions">
                <button
                  className="ea-cta"
                  type="button"
                  onClick={() => handleCtaClick({
                    elementName: 'contactar_partner',
                    subsection2: 'hero'
                  })}
                >
                  Quiero hablar de una colaboración
                  <span aria-hidden="true">-&gt;</span>
                </button>
                <button
                  className="ea-cta-outline ea-cta-outline-dark"
                  type="button"
                  onClick={() => handleCtaClick({
                    elementName: 'ver_servicios_partner',
                    subsection2: 'hero',
                    targetId: 'partner-agencias-servicios'
                  })}
                >
                  Ver qué puedo ofrecer a mi agencia
                </button>
              </div>
              <p className="ea-trust">
                Pensado para agencias de marketing, estudios web y profesionales que venden webs,
                campañas o estrategia digital.
              </p>
            </div>

            <div className="ea-reveal">
              <div className="ea-funnel-card" aria-label="Capa técnica de medición para agencias">
                <div className="ea-funnel-head">
                  <div>
                    <p>La capa que tu cliente no ve</p>
                    <strong>Sin medición, tu agencia pierde argumentos</strong>
                  </div>
                  <span>Reporting débil</span>
                </div>
                <div className="ea-funnel">
                  <div className="ea-funnel-stage">Campana / Web</div>
                  <div className="ea-funnel-stage">Eventos</div>
                  <div className="ea-funnel-stage">Conversiones</div>
                  <div className="ea-leak" aria-hidden="true">
                    <span className="ea-leak-line" />
                    <span>dato perdido</span>
                    <span className="ea-leak-dot" />
                  </div>
                </div>
                <div className="ea-funnel-notes">
                  <div><strong>Medición</strong><span>qué ocurre</span></div>
                  <div><strong>Trazabilidad</strong><span>de dónde viene</span></div>
                  <div><strong>Reporting</strong><span>qué demostrar</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ea-section ea-section-muted" data-section="problema">
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading">
              <h2>El problema no es lanzar campañas. El problema es demostrar qué generan.</h2>
              <p>Cuando la medición no está completa, el valor real de tu trabajo queda a medias.</p>
            </div>
            <div className="ea-card-grid">
              <article className="ea-reveal ea-card">
                <span className="ea-icon">?</span>
                <h3>Tus clientes preguntan por resultados, pero muchas veces la medición no está completa</h3>
              </article>
              <article className="ea-reveal ea-card">
                <span className="ea-icon">+</span>
                <h3>Formularios, WhatsApp, llamadas, CTAs o secciones clave no siempre se miden bien</h3>
              </article>
              <article className="ea-reveal ea-card">
                <span className="ea-icon">%</span>
                <h3>Google Ads puede estar optimizando con conversiones mal definidas</h3>
              </article>
            </div>
          </div>
        </section>

        <section className="ea-section" data-section="solucion">
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading ea-centered">
              <h2>Yo cubro la capa técnica de medición para que tu agencia pueda vender más valor</h2>
              <p>
                Trabajo como partner white-label para configurar, revisar y documentar la medición de tus clientes.
                Tu agencia mantiene la relación comercial y yo me encargo de la parte técnica.
              </p>
            </div>
            <div className="ea-steps">
              <article className="ea-reveal ea-step">
                <span className="ea-step-number">1</span>
                <h3>Reviso la medición</h3>
                <p>Detecto qué se mide, qué falta y qué conversiones pueden estar contaminando las decisiones.</p>
              </article>
              <article className="ea-reveal ea-step">
                <span className="ea-step-number">2</span>
                <h3>Implemento y documento</h3>
                <p>Configuro GA4, GTM, eventos, conversiones y trazabilidad para que el equipo lo pueda explicar.</p>
              </article>
              <article className="ea-reveal ea-step">
                <span className="ea-step-number">3</span>
                <h3>Ayudo a demostrar valor</h3>
                <p>Preparo dashboards y reporting para que la agencia pueda tomar decisiones y presentar resultados.</p>
              </article>
            </div>
          </div>
        </section>

        <section
          id="partner-agencias-servicios"
          className="ea-section ea-section-muted"
          data-section="servicios"
        >
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading ea-centered">
              <h2>Qué puedes ofrecer a tus clientes con este servicio</h2>
            </div>
            <div className="ea-card-grid">
              <article className="ea-reveal ea-card">
                <span className="ea-icon">1</span>
                <h3>Auditoría de medición actual</h3>
                <p>Revisión de GA4, GTM, conversiones, formularios, WhatsApp, llamadas y CTAs.</p>
              </article>
              <article className="ea-reveal ea-card">
                <span className="ea-icon">2</span>
                <h3>Implementación de analítica y conversiones</h3>
                <p>Configuración de GA4, Google Tag Manager, eventos de conversión y conexión con Google Ads.</p>
              </article>
              <article className="ea-reveal ea-card">
                <span className="ea-icon">3</span>
                <h3>Dashboards y reporting white-label</h3>
                <p>Looker Studio o Power BI, informes bajo tu marca y soporte técnico para propuestas comerciales.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="ea-section" data-section="para_quien">
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading ea-centered">
              <h2>Pensado para equipos que venden webs, campañas o estrategia digital</h2>
            </div>
            <div className="ea-fit-grid">
              <div className="ea-reveal ea-fit-card ea-fit-yes">
                <h3>Encaja si eres...</h3>
                <ul>
                  <li>Agencia de marketing</li>
                  <li>Estudio web</li>
                  <li>Freelancer que crea webs o landings</li>
                  <li>Consultor SEO/SEM o especialista en paid media</li>
                </ul>
              </div>
              <div className="ea-reveal ea-fit-card ea-fit-no">
                <h3>Especialmente útil si...</h3>
                <ul>
                  <li>Gestionas ecommerce, campañas o captación de leads</li>
                  <li>Necesitas justificar mejor el impacto de tus acciones</li>
                  <li>Quieres sumar analítica sin contratar un perfil técnico fijo</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section
          className="ea-section ea-section-muted"
          data-section="beneficios"
        >
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading ea-centered">
              <h2>Lo que gana tu agencia</h2>
            </div>
            <div className="ea-card-grid">
              <article className="ea-reveal ea-card">
                <span className="ea-icon">+</span>
                <h3>Vendes un servicio más completo</h3>
                <p>Aumentas el ticket medio y refuerzas la confianza con datos claros.</p>
              </article>
              <article className="ea-reveal ea-card">
                <span className="ea-icon">=</span>
                <h3>Reduces reporting manual</h3>
                <p>Evitas depender de instalaciones básicas de Analytics y reportes poco accionables.</p>
              </article>
              <article className="ea-reveal ea-card">
                <span className="ea-icon">W</span>
                <h3>Tienes un partner bajo tu marca</h3>
                <p>Apoyo técnico sin coste fijo de plantilla y sin romper la relación con tu cliente.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="ea-section" data-section="autoridad">
          <div className="ea-container ea-narrow ea-centered">
            <div className="ea-reveal">
              <p className="ea-kicker">Experiencia</p>
              <h2>Buenas prácticas de medición de entornos enterprise, adaptadas a proyectos reales</h2>
              <p className="ea-about">
                Vengo de entornos enterprise trabajando con Adobe Analytics, Adobe Launch, Tealium,
                GA4, GTM y Power BI. Aplico buenas prácticas de medición, capa de datos y trazabilidad
                a proyectos de agencias, pymes y negocios digitales.
              </p>
            </div>
          </div>
        </section>

        <section
          className="ea-section ea-section-muted"
          data-section="modalidad"
        >
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading ea-centered ea-price-heading">
              <h2>Modalidades flexibles según el cliente y el momento</h2>
            </div>
            <div className="ea-reveal ea-price-card">
              <div className="ea-price-main">
                <p>Sin precios cerrados de forma agresiva</p>
                <h3>Flexible</h3>
                <span>
                  Podemos empezar con una auditoría puntual, una implementación concreta o un modelo
                  de colaboración recurrente para varios clientes.
                </span>
              </div>
              <div className="ea-price-includes">
                <h4>Opciones habituales</h4>
                <ul>
                  <li>Auditoría de medición</li>
                  <li>Implementación básica</li>
                  <li>Implementación Pro</li>
                  <li>Dashboard</li>
                  <li>Partner mensual para agencias</li>
                </ul>
                <strong>La agencia conserva la relación comercial. DataDos opera como apoyo técnico.</strong>
              </div>
              <button
                className="ea-cta ea-price-cta"
                type="button"
                onClick={() => handleCtaClick({
                  elementName: 'contactar_partner',
                  subsection2: 'modalidad'
                })}
              >
                Quiero hablar de una colaboración
                <span aria-hidden="true">-&gt;</span>
              </button>
            </div>
          </div>
        </section>

        <section className="ea-final-cta" data-section="cta_final">
          <div className="ea-container ea-narrow ea-centered">
            <div className="ea-reveal">
              <h2>Tu agencia vende la web o la campaña. Yo hago que puedas demostrar con datos qué está funcionando.</h2>
              <p>Hablemos de una colaboración puntual o recurrente bajo tu marca.</p>
              <div className="ea-final-actions">
                <button
                  className="ea-cta ea-cta-light"
                  type="button"
                  onClick={() => handleCtaClick({
                    elementName: 'contactar_partner',
                    subsection2: 'cta_final'
                  })}
                >
                  Quiero hablar de una colaboración
                  <span aria-hidden="true">-&gt;</span>
                </button>
                <a
                  className="ea-cta-outline"
                  href="https://wa.me/34626852662"
                  onClick={() => pushDataLayerEvent({
                    event: 'click_cta',
                    subsection2: 'cta_final',
                    elementCategory: 'cta',
                    elementName: 'whatsapp'
                  })}
                >
                  WhatsApp
                </a>
                <a
                  className="ea-cta-outline"
                  href="mailto:info@datados.es"
                  onClick={() => pushDataLayerEvent({
                    event: 'click_cta',
                    subsection2: 'cta_final',
                    elementCategory: 'cta',
                    elementName: 'email'
                  })}
                >
                  Email
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="partner-agencias-contact-form" className="ea-section ea-contact-section" data-section="formulario_contacto">
          <div className="ea-container ea-narrow">
            <div className="ea-reveal ea-section-heading ea-centered">
              <h2>Cuéntame cómo trabaja tu agencia</h2>
              <p>Te respondo personalmente para ver si encaja una colaboración puntual o recurrente.</p>
            </div>

            <div className="ea-reveal ea-form-card">
              {status === 'success' ? (
                <div className="ea-form-message ea-form-message-success" data-section="solicitud_enviada">
                  <h3>Solicitud enviada correctamente</h3>
                  <p>He recibido tu mensaje. Te responderé personalmente lo antes posible.</p>
                  <button
                    className="ea-text-button"
                    type="button"
                    onClick={() => {
                      pushDataLayerEvent({
                        event: 'button',
                        subsection2: 'solicitud_enviada',
                        elementCategory: 'cta',
                        elementName: 'enviar_otra_solicitud'
                      });
                      setStatus('idle');
                    }}
                  >
                    Enviar otra solicitud
                  </button>
                </div>
              ) : (
                <form id="form-partner-agencias" onSubmit={handleSubmit} onFocusCapture={handleFormInteraction}>
                  <input type="text" name="b_name" className="ea-honeypot" tabIndex="-1" autoComplete="off" />

                  <div className="ea-form-grid">
                    <label>
                      <span>Nombre</span>
                      <input type="text" name="name" required />
                    </label>
                    <label>
                      <span>Empresa / agencia</span>
                      <input type="text" name="company" required />
                    </label>
                  </div>

                  <div className="ea-form-grid">
                    <label>
                      <span>Email</span>
                      <input type="email" name="email" required />
                    </label>
                    <label>
                      <span>Teléfono</span>
                      <input type="tel" name="phone" />
                    </label>
                  </div>

                  <label>
                    <span>Tipo de colaboración</span>
                    <select name="service_interest" defaultValue="partner_mensual">
                      <option value="auditoria">Auditoría de medición</option>
                      <option value="implementacion">Implementación</option>
                      <option value="dashboard">Dashboard</option>
                      <option value="partner_mensual">Partner mensual</option>
                      <option value="otro">Otro</option>
                    </select>
                  </label>

                  <label>
                    <span>Mensaje</span>
                    <textarea name="message" rows="5" />
                  </label>

                  {status === 'error' && (
                    <div className="ea-form-error">
                      No se pudo enviar el formulario. Inténtelo de nuevo o escríbeme a info@datados.es.
                    </div>
                  )}

                  <div className="ea-form-consent-group">
                    <label className={`ea-form-consent${showError ? ' ea-form-consent-error' : ''}`}>
                      <input
                        type="checkbox"
                        name="privacy_policy"
                        checked={privacyAccepted}
                        onChange={(event) => {
                          setPrivacyAccepted(event.target.checked);
                          if (event.target.checked) {
                            setShowError(false);
                          }
                        }}
                      />
                      <span>
                        He leído y acepto la{' '}
                        <a href="/politica-privacidad">
                          Política de Privacidad
                        </a>
                      </span>
                    </label>

                    {showError && (
                      <p className="ea-form-consent-message">
                        Por favor, acepta la política de privacidad para continuar
                      </p>
                    )}
                  </div>

                  <button className="ea-cta ea-form-submit" type="submit" disabled={status === 'loading'}>
                    {status === 'loading' ? 'Enviando solicitud...' : 'Enviar solicitud'}
                  </button>

                  <p className="ea-form-legal">
                    Al enviar este formulario, aceptas que DataDos use estos datos únicamente para responder a tu solicitud.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="ea-footer">
        <div className="ea-container ea-footer-content">
          <div>
            <strong>DataDos</strong>
            <p>info@datados.es · admin@datados.es · Teléfono: +34 626 852 662</p>
            <a
              href="mailto:info@datados.es"
              onClick={() => pushDataLayerEvent({
                event: 'click_cta',
                subsection2: 'footer',
                elementCategory: 'cta',
                elementName: 'email'
              })}
            >
              info@datados.es
            </a>
          </div>
          <div className="ea-footer-links">
            <a href="/aviso-legal">Aviso legal</a>
            <a href="/politica-privacidad">Política de privacidad</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PartnerAgenciesLanding;
